/**
 * Model-specific filter definitions
 * This file contains predefined filter configurations for each model type
 */

export const getModelFilters = (modelName, t) => {
    const filterDefinitions = {
        resources: [
            {
                key: 'category_ids',
                label: t('resources.filters.category'),
                type: 'multiselect',
                icon: 'layer-group',
                // options will be auto-populated by Collection using categories data
            },
            {
                key: 'location_id',
                label: t('resources.filters.location'),
                type: 'select',
                icon: 'map-marker-alt',
                // options will be auto-populated by Collection using locations data
            },
            {
                key: 'tag_ids',
                label: t('resources.filters.tags'),
                type: 'multiselect',
                icon: 'tags',
                // options will be auto-populated by Collection using tags data
            },
            {
                key: 'availability',
                label: t('resources.filters.availability'),
                type: 'select',
                options: [
                    { value: '', label: t('resources.filters.allResources') },
                    { value: 'available', label: t('resources.filters.availableOnly') },
                    { value: 'unavailable', label: t('resources.filters.unavailableOnly') }
                ],
                icon: 'check'
            },
            {
                key: 'consumable',
                label: t('resources.filters.type'),
                type: 'select',
                options: [
                    { value: '', label: t('resources.filters.allTypes') },
                    { value: 'true', label: t('resources.filters.consumableOnly') },
                    { value: 'false', label: t('resources.filters.borrowableOnly') }
                ],
                icon: 'cubes'
            }
        ],

        categories: [],

        locations: [],

        tags: [
            {
                key: 'user_specific',
                label: t('tags.filters.scope'),
                type: 'select',
                options: [
                    { value: '', label: t('tags.filters.allTags') },
                    { value: 'user', label: t('tags.filters.userTags') },
                    { value: 'system', label: t('tags.filters.systemTags') }
                ],
                icon: 'user'
            }
        ],

        borrowings: [
            {
                key: 'category_ids',
                label: t('borrowing.category'),
                type: 'multiselect',
                icon: 'layer-group',
                // options will be auto-populated by Collection using categories data
            }
        ],

        reservations: [
            {
                key: 'status',
                label: t('reservation.status'),
                type: 'select',
                options: [
                    { value: '', label: t('reservation.all') },
                    { value: 'upcoming', label: t('reservation.upcoming') },
                    { value: 'active', label: t('reservation.active') },
                    { value: 'past', label: t('reservation.past') }
                ],
                icon: 'clock'
            },
            {
                key: 'resource_id',
                label: t('reservation.resource'),
                type: 'select',
                icon: 'cubes',
                // options will be auto-populated by Collection using resources data
            }
        ],

        consumptions: [
            {
                key: 'category_ids',
                label: t('consumption.category'),
                type: 'multiselect',
                icon: 'layer-group',
                // options will be auto-populated by Collection using categories data
            },
            {
                key: 'date_range',
                label: t('consumption.dateRange'),
                type: 'select',
                options: [
                    { value: '', label: t('consumption.allTime') },
                    { value: 'today', label: t('consumption.today') },
                    { value: 'week', label: t('consumption.thisWeek') },
                    { value: 'month', label: t('consumption.thisMonth') }
                ],
                icon: 'calendar'
            }
        ],

        refills: [
            {
                key: 'category_ids',
                label: t('refill.category'),
                type: 'multiselect',
                icon: 'layer-group',
                // options will be auto-populated by Collection using categories data
            },
            {
                key: 'date_range',
                label: t('refill.dateRange'),
                type: 'select',
                options: [
                    { value: '', label: t('refill.allTime') },
                    { value: 'today', label: t('refill.today') },
                    { value: 'week', label: t('refill.thisWeek') },
                    { value: 'month', label: t('refill.thisMonth') }
                ],
                icon: 'calendar'
            }
        ]
    };

    return filterDefinitions[modelName] || [];
};

/**
 * Get default sort fields for a model type
 */
export const getModelSortFields = (modelName) => {
    const sortFieldsMap = {
        resources: [
            { value: 'name', label: 'Name A-Z' },
            { value: '-name', label: 'Name Z-A' },
            { value: 'created_at', label: 'Oldest First' },
            { value: '-created_at', label: 'Newest First' },
            { value: 'availability_status', label: 'Available First' }
        ],
        categories: [
            { value: 'name', label: 'Name A-Z' },
            { value: '-name', label: 'Name Z-A' },
            { value: 'resource_count', label: 'Fewest Resources' },
            { value: '-resource_count', label: 'Most Resources' }
        ],
        locations: [
            { value: 'name', label: 'Name A-Z' },
            { value: '-name', label: 'Name Z-A' },
            { value: 'created_at', label: 'Oldest First' },
            { value: '-created_at', label: 'Newest First' }
        ],
        tags: [
            { value: 'name', label: 'Name A-Z' },
            { value: '-name', label: 'Name Z-A' },
            { value: 'resource_count', label: 'Fewest Resources' },
            { value: '-resource_count', label: 'Most Resources' }
        ],
        borrowings: [
            { value: 'borrowed_at', label: 'Oldest First' },
            { value: '-borrowed_at', label: 'Newest First' },
            { value: 'expected_return_date', label: 'Due Soon' },
            { value: '-expected_return_date', label: 'Due Later' }
        ],
        reservations: [
            { value: 'reserved_from', label: 'Earliest First' },
            { value: '-reserved_from', label: 'Latest First' },
            { value: 'created_at', label: 'Oldest First' },
            { value: '-created_at', label: 'Newest First' }
        ],
        consumptions: [
            { value: 'consumed_at', label: 'Oldest First' },
            { value: '-consumed_at', label: 'Newest First' },
            { value: 'quantity', label: 'Smallest Amount' },
            { value: '-quantity', label: 'Largest Amount' }
        ],
        refills: [
            { value: 'refilled_at', label: 'Oldest First' },
            { value: '-refilled_at', label: 'Newest First' },
            { value: 'quantity', label: 'Smallest Amount' },
            { value: '-quantity', label: 'Largest Amount' }
        ]
    };

    return sortFieldsMap[modelName] || [
        { value: 'name', label: 'Name A-Z' },
        { value: '-name', label: 'Name Z-A' }
    ];
};


/**
 * Get page size options for a model type
 */
export const getModelPageSizeOptions = (modelName) => {
    return [24, 48, 96];
};

/**
 * Get default page size for a model type
 */
export const getModelDefaultPageSize = (modelName) => {

    return 24;
};
